import { create } from "zustand";
import { createSelectors } from "../createSelectors";

/**
 * Workflows async de catalog (variants, options) que están corriendo en el
 * backend (Temporal) para un producto específico.
 *
 * **Por qué existe este store**:
 *
 * El backend trd-core usa Temporal workflows con IDs DETERMINÍSTICOS basados
 * en `${workflowType}-${businessId}-${productId}`. Eso significa que:
 *
 * 1. NO se puede tener 2 workflows del mismo tipo sobre el mismo producto
 *    al mismo tiempo. Si el frontend manda 2 POSTs rápidos seguidos:
 *    → segundo workflow es rechazado con HTTP 500
 *    → mensaje: "Workflow execution already started"
 *
 * 2. El backend NO emite socket events cuando el workflow termina (a
 *    diferencia de pricing). La única forma de saber que terminó es
 *    polling al GraphQL hasta detectar el cambio propagado.
 *
 * **Cómo lo usamos**:
 *
 * - Antes de mandar una mutation, chequear `isProductBusy(productId)`.
 *   Si es true, NO mandar y mostrar warning al user.
 * - Mientras la mutation está activa: `start(productId, {...})` → marca
 *   el producto como busy, muestra toast persistente, registra el polling.
 * - Cuando el polling detecta propagación o se cumple el timeout: `end(productId)`.
 *
 * Ver: backend-cqrs-eventual-consistency en CLAUDE.md.
 */
export interface PendingProductWorkflow {
  productId: string;
  /** Tipo de operación, para el mensaje del toast y debugging. */
  operationLabel: string;
  /** Timestamp de inicio. */
  startedAt: number;
  /** Id del toast persistente "Aplicando cambios…". */
  toastId?: string;
  /** Timer del timeout fallback (para clearear si termina antes). */
  timeoutId?: ReturnType<typeof setTimeout>;
}

interface PendingProductWorkflowsState {
  pending: Record<string, PendingProductWorkflow>;
  /**
   * Acciones encoladas por producto, en orden FIFO. Cuando el workflow en
   * curso para un product termina (`end()`), se desencola la próxima y se
   * dispara automáticamente. Esto da UX tipo Linear: el usuario clickea
   * "Agregar opción" mientras hay otra agregándose y la nueva se aplica
   * sola cuando la anterior termina, sin botones bloqueados.
   */
  queued: Record<string, Array<() => void>>;
  start: (workflow: PendingProductWorkflow) => void;
  /**
   * Cierra el workflow activo de un producto. Idempotente — llamar varias
   * veces (ej: por timeout fallback + por socket) no rompe. Si hay acciones
   * encoladas para ese product, desencola la próxima y la ejecuta vía
   * microtask para que el setState se aplique primero.
   */
  end: (productId: string) => void;
  /** Encola una acción para ejecutar cuando el workflow activo termine. */
  enqueue: (productId: string, action: () => void) => void;
}

const store = create<PendingProductWorkflowsState>((set, get) => ({
  pending: {},
  queued: {},

  start: (workflow) =>
    set((state) => ({
      pending: { ...state.pending, [workflow.productId]: workflow },
    })),

  end: (productId) => {
    const workflow = get().pending[productId];
    // Idempotente: si ya no está pending, no hay nada que hacer.
    if (!workflow) return;
    if (workflow.timeoutId) clearTimeout(workflow.timeoutId);

    // Snapshot de la queue ANTES del setState para no perder acciones si
    // alguien encola justo en este tick.
    const queue = get().queued[productId] ?? [];
    const next = queue[0];
    const restOfQueue = queue.slice(1);

    set((state) => {
      const nextPending = { ...state.pending };
      delete nextPending[productId];
      const nextQueued = { ...state.queued };
      if (restOfQueue.length === 0) {
        delete nextQueued[productId];
      } else {
        nextQueued[productId] = restOfQueue;
      }
      return { pending: nextPending, queued: nextQueued };
    });

    // Disparar la próxima encolada después del setState. queueMicrotask
    // garantiza que el store ya esté limpio cuando la acción se ejecute
    // (así el guard de la mutation no vuelve a encolar contra sí mismo).
    if (next) queueMicrotask(next);
  },

  enqueue: (productId, action) =>
    set((state) => ({
      queued: {
        ...state.queued,
        [productId]: [...(state.queued[productId] ?? []), action],
      },
    })),
}));

export const usePendingProductWorkflowsStore = createSelectors(store);

/**
 * Hook conveniente: ¿hay un workflow pendiente para este producto?
 *
 * Usar en componentes que tienen botones de mutation (agregar variante,
 * eliminar opción, etc.) para deshabilitarlos mientras hay un workflow
 * pendiente sobre el mismo producto:
 *
 * ```tsx
 * const isBusy = useIsProductBusy(productId);
 * <Button disabled={isBusy}>Agregar opción</Button>
 * ```
 */
export const useIsProductBusy = (productId: string | undefined): boolean =>
  usePendingProductWorkflowsStore((state) =>
    productId ? !!state.pending[productId] : false
  );
